import React, { useState, useEffect, useCallback } from "react";
import { motion } from "motion/react";
import {
  FileText, CheckCircle, ListChecks, Link2, Save, Loader2,
  Send, Eye, Lock, Plus, Trash2, X
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useAutoSave } from "../../hooks/useAutoSave";
import AutoSaveIndicator from "./AutoSaveIndicator";

export default function SessionDeckBuilder({ booking, expertId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const [form, setForm] = useState({
    session_summary: "",
    action_plan: [],
    resource_links: [],
    private_notes: "",
    status: "draft"
  });

  const [newStep, setNewStep] = useState("");
  const [newLink, setNewLink] = useState({ title: "", url: "" });

  const buildPayload = useCallback((formData) => ({
    session_summary: formData.session_summary,
    action_plan: formData.action_plan,
    resource_links: formData.resource_links,
    private_notes: formData.private_notes,
    status: formData.status
  }), []);

  const buildInsertPayload = useCallback((formData) => ({
    session_summary: formData.session_summary,
    action_plan: formData.action_plan,
    resource_links: formData.resource_links,
    private_notes: formData.private_notes,
    status: formData.status || "draft",
    booking_id: booking.id,
    expert_id: booking.expertId || expertId,
    client_id: booking.clientId,
    expert_name: booking.expertName || "",
    client_name: booking.clientName || "",
    session_date: booking.date || "",
    session_topic: booking.topic || ""
  }), [booking, expertId]);

  const {
    autoSaving,
    lastAutoSaved,
    restoredFromDraft,
    setRestoredFromDraft,
    scheduleAutoSave,
    cancelAutoSave,
    markSaved,
    loadFromLocal
  } = useAutoSave({
    table: "session_materials",
    bookingId: booking.id,
    form,
    buildPayload,
    buildInsertPayload,
    enabled: !loading
  });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await supabase
          .from("session_materials")
          .select("*")
          .eq("booking_id", booking.id)
          .maybeSingle();
        if (data) {
          setForm({
            session_summary: data.session_summary || "",
            action_plan: Array.isArray(data.action_plan) ? data.action_plan : [],
            resource_links: Array.isArray(data.resource_links) ? data.resource_links : [],
            private_notes: data.private_notes || "",
            status: data.status || "draft"
          });
        } else {
          const localDraft = loadFromLocal();
          if (localDraft) {
            setForm(localDraft);
            setRestoredFromDraft(true);
          }
        }
      } catch (err) {
        console.warn("Failed to load session materials:", err);
        const localDraft = loadFromLocal();
        if (localDraft) {
          setForm(localDraft);
          setRestoredFromDraft(true);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [booking.id, loadFromLocal, setRestoredFromDraft]);

  const handleSave = async (publish = false) => {
    cancelAutoSave();
    if (publish) setPublishing(true);
    else setSaving(true);

    const payload = {
      session_summary: form.session_summary,
      action_plan: form.action_plan,
      resource_links: form.resource_links,
      private_notes: form.private_notes,
      status: publish ? "published" : "draft",
      published_at: publish ? new Date().toISOString() : null,
      updated_at: new Date().toISOString()
    };

    const { data: existing } = await supabase
      .from("session_materials")
      .select("id")
      .eq("booking_id", booking.id)
      .maybeSingle();

    let result;
    if (existing) {
      const { data, error } = await supabase
        .from("session_materials")
        .update(payload)
        .eq("id", existing.id)
        .select()
        .single();
      result = error ? null : data;
    } else {
      const { data, error } = await supabase
        .from("session_materials")
        .insert({
          ...payload,
          booking_id: booking.id,
          expert_id: booking.expertId || expertId,
          client_id: booking.clientId,
          expert_name: booking.expertName || "",
          client_name: booking.clientName || "",
          session_date: booking.date || "",
          session_topic: booking.topic || ""
        })
        .select()
        .single();
      result = error ? null : data;
    }

    setSaving(false);
    setPublishing(false);

    if (result) {
      setForm((prev) => ({ ...prev, status: result.status }));
      markSaved();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (onSaved) onSaved(result);
    }
  };

  const updateForm = (updates) => {
    const newForm = { ...form, ...updates };
    setForm(newForm);
    scheduleAutoSave(newForm);
  };

  const addStep = () => {
    if (!newStep.trim()) return;
    updateForm({
      action_plan: [...form.action_plan, { text: newStep.trim(), done: false }]
    });
    setNewStep("");
  };

  const removeStep = (idx) => {
    updateForm({
      action_plan: form.action_plan.filter((_, i) => i !== idx)
    });
  };

  const addLink = () => {
    if (!newLink.title.trim()) return;
    updateForm({
      resource_links: [...form.resource_links, { ...newLink }]
    });
    setNewLink({ title: "", url: "" });
  };

  const removeLink = (idx) => {
    updateForm({
      resource_links: form.resource_links.filter((_, i) => i !== idx)
    });
  };

  if (loading) {
    return (
      <div id="session-deck-builder_loading" className="p-6 text-center">
        <Loader2 className="w-8 h-8 text-primary-400 animate-spin mx-auto mb-3" />
        <p className="text-lg font-extralight text-neutral-400">Загрузка...</p>
      </div>
    );
  }

  return (
    <motion.div
      id="session-deck-builder_root"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 rounded-md bg-neutral-900 border border-neutral-800"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-medium text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary-400" />
            Session Deck
          </h3>
          <p className="text-lg font-extralight text-neutral-400 mt-1">
            {booking.clientName} — {booking.topic || "Без темы"} — {booking.date}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AutoSaveIndicator
            autoSaving={autoSaving}
            lastAutoSaved={lastAutoSaved}
            restoredFromDraft={restoredFromDraft}
            onDismissRestore={() => setRestoredFromDraft(false)}
          />
          {form.status === "published" && (
            <span className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-accent-500/10 text-accent-400 text-lg font-extralight">
              <Eye className="w-4 h-4" />
              Опубликовано
            </span>
          )}
          {onClose && (
            <button onClick={onClose} className="p-2 rounded-md hover:bg-neutral-800 text-neutral-400 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <label className="flex items-center gap-2 text-lg font-extralight text-neutral-300 mb-2">
            <FileText className="w-4 h-4 text-primary-400" />
            Итог сессии
          </label>
          <textarea
            value={form.session_summary}
            onChange={(e) => updateForm({ session_summary: e.target.value })}
            rows={4}
            placeholder="Краткий итог встречи, основные обсужденные темы и договоренности"
            className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500 resize-none transition-colors"
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-lg font-extralight text-neutral-300 mb-2">
            <ListChecks className="w-4 h-4 text-accent-400" />
            План действий для клиента
          </label>
          <div className="space-y-2 mb-3">
            {form.action_plan.map((step, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-md bg-neutral-800/50">
                <CheckCircle className="w-4 h-4 text-accent-400 flex-shrink-0" />
                <span className="text-lg font-extralight text-neutral-200 flex-1">{step.text}</span>
                <button onClick={() => removeStep(i)} className="p-1 rounded hover:bg-red-500/10 text-neutral-500 hover:text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newStep}
              onChange={(e) => setNewStep(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addStep()}
              placeholder="Добавить шаг..."
              className="flex-1 px-4 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-accent-500 transition-colors"
            />
            <button onClick={addStep} className="px-4 py-2 rounded-md bg-accent-500/10 text-accent-400 hover:bg-accent-500/20 transition-colors">
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-lg font-extralight text-neutral-300 mb-2">
            <Link2 className="w-4 h-4 text-blue-400" />
            Полезные ресурсы
          </label>
          <div className="space-y-2 mb-3">
            {form.resource_links.map((link, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-md bg-neutral-800/50">
                <Link2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-extralight text-neutral-200 truncate">{link.title}</p>
                  {link.url && (
                    <p className="text-lg font-extralight text-neutral-500 truncate">{link.url}</p>
                  )}
                </div>
                <button onClick={() => removeLink(i)} className="p-1 rounded hover:bg-red-500/10 text-neutral-500 hover:text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newLink.title}
              onChange={(e) => setNewLink((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Название ресурса"
              className="flex-1 px-4 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-blue-500 transition-colors"
            />
            <input
              type="text"
              value={newLink.url}
              onChange={(e) => setNewLink((prev) => ({ ...prev, url: e.target.value }))}
              placeholder="URL (необязательно)"
              className="flex-1 px-4 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-blue-500 transition-colors"
            />
            <button onClick={addLink} className="px-4 py-2 rounded-md bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors">
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-lg font-extralight text-neutral-300 mb-2">
            <Lock className="w-4 h-4 text-yellow-400" />
            Приватные заметки
            <span className="px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 text-lg font-extralight">
              Только для вас
            </span>
          </label>
          <textarea
            value={form.private_notes}
            onChange={(e) => updateForm({ private_notes: e.target.value })}
            rows={2}
            placeholder="Заметки, которые видите только вы..."
            className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-yellow-500 resize-none transition-colors"
          />
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 pt-4 border-t border-neutral-800">
        <div>
          {saved && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-2 text-accent-400"
            >
              <CheckCircle className="w-5 h-5" />
              <span className="text-lg font-extralight">Сохранено</span>
            </motion.div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleSave(false)}
            disabled={saving || publishing}
            className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-neutral-700 text-neutral-300 hover:text-white hover:border-neutral-500 disabled:opacity-50 text-lg font-extralight transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Сохранить черновик
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving || publishing || !form.session_summary.trim()}
            className="flex items-center gap-2 px-6 py-2.5 rounded-md bg-accent-600 hover:bg-accent-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
          >
            {publishing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            Отправить клиенту
          </button>
        </div>
      </div>
    </motion.div>
  );
}
